import React, { useEffect, useRef, useState } from 'react';
import {
    Bell,
    Check,
    ChevronRight,
    Clock3,
    Pause,
    Play,
    Plus,
    RotateCcw,
    Settings,
    Sparkles,
    Timer as TimerIcon,
    Trash2,
    Wind
} from 'lucide-react';
import ScreenLayout from './ScreenLayout';

const DEFAULT_DURATION = 5 * 60;
const TIMER_CIRCUMFERENCE = 552.92;
const durationPresets = [1, 3, 5, 10, 15];

const initialPrayerTopics = [
    'Personal relationship with God',
    'Family and loved ones',
    'Church and community',
    'Those in need',
    'World events and peace'
];

const PrayerTimer = () => {
    const [duration, setDuration] = useState(DEFAULT_DURATION);
    const [timeLeft, setTimeLeft] = useState(DEFAULT_DURATION);
    const [isActive, setIsActive] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [customMinutes, setCustomMinutes] = useState(5);
    const [customSeconds, setCustomSeconds] = useState(0);
    const [prayerTopics, setPrayerTopics] = useState(initialPrayerTopics);
    const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
    const bellSound = useRef(null);
    const timerRef = useRef(null);

    useEffect(() => {
        bellSound.current = new Audio('https://soundbible.com/grab.php?id=1531&type=mp3');

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    useEffect(() => {
        if (!isActive || isPaused) {
            clearInterval(timerRef.current);
            return undefined;
        }

        timerRef.current = setInterval(() => {
            setTimeLeft((remainingTime) => {
                if (remainingTime <= 1) {
                    if (bellSound.current) {
                        bellSound.current.currentTime = 0;
                        bellSound.current.play().catch(() => {});
                    }
                    clearInterval(timerRef.current);
                    setIsActive(false);
                    setCurrentTopicIndex((index) => (index + 1) % prayerTopics.length);
                    return 0;
                }

                return remainingTime - 1;
            });
        }, 1000);

        return () => clearInterval(timerRef.current);
    }, [isActive, isPaused, prayerTopics.length]);

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = time % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const resetTimer = () => {
        clearInterval(timerRef.current);
        setIsActive(false);
        setIsPaused(false);
        setTimeLeft(duration);
    };

    const selectDuration = (minutes) => {
        const nextDuration = minutes * 60;
        clearInterval(timerRef.current);
        setDuration(nextDuration);
        setTimeLeft(nextDuration);
        setCustomMinutes(minutes);
        setCustomSeconds(0);
        setIsActive(false);
        setIsPaused(false);
    };

    const saveDuration = () => {
        const minutes = Number.parseInt(customMinutes, 10) || 0;
        const seconds = Number.parseInt(customSeconds, 10) || 0;
        const nextDuration = Math.max(1, (minutes * 60) + seconds);

        clearInterval(timerRef.current);
        setDuration(nextDuration);
        setTimeLeft(nextDuration);
        setCustomMinutes(Math.floor(nextDuration / 60));
        setCustomSeconds(nextDuration % 60);
        setIsActive(false);
        setIsPaused(false);
        setShowSettings(false);
    };

    const addPrayerTopic = () => {
        setPrayerTopics([...prayerTopics, 'New prayer focus']);
    };

    const updatePrayerTopic = (index, value) => {
        setPrayerTopics(prayerTopics.map((topic, topicIndex) => (
            topicIndex === index ? value : topic
        )));
    };

    const removePrayerTopic = (index) => {
        if (prayerTopics.length <= 1) return;

        const updatedTopics = prayerTopics.filter((_, topicIndex) => topicIndex !== index);
        setPrayerTopics(updatedTopics);
        if (currentTopicIndex >= updatedTopics.length) setCurrentTopicIndex(0);
    };

    const selectTopic = (index) => {
        setCurrentTopicIndex(index);
        resetTimer();
    };

    const progressPercentage = duration > 0 ? ((duration - timeLeft) / duration) * 100 : 0;
    const progressOffset = TIMER_CIRCUMFERENCE - (TIMER_CIRCUMFERENCE * progressPercentage) / 100;
    const timerStatus = isActive && !isPaused ? 'Prayer in progress' : isPaused ? 'Paused' : timeLeft === 0 ? 'Complete' : 'Ready when you are';

    return (
        <ScreenLayout title="5 Minute Prayer" eyebrow="Create stillness" icon={TimerIcon} tone="night">
            <div className="prayer-timer-page">
                <section className={`prayer-timer-stage ${isActive && !isPaused ? 'is-active' : ''}`}>
                    <div className="prayer-timer-stage-copy">
                        <p className="prayer-timer-kicker"><Sparkles size={14} /> A quiet practice</p>
                        <h1>Make room for stillness.</h1>
                        <p>Choose a focus, take one slow breath, and let these minutes belong only to prayer.</p>

                        <div className="prayer-timer-presets" aria-label="Prayer duration">
                            {durationPresets.map((minutes) => (
                                <button
                                    type="button"
                                    key={minutes}
                                    className={duration === minutes * 60 ? 'is-selected' : ''}
                                    onClick={() => selectDuration(minutes)}
                                >
                                    <strong>{minutes}</strong>
                                    <span>min</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="prayer-timer-clock">
                        <div className="prayer-timer-ring">
                            <svg viewBox="0 0 200 200" role="img" aria-label={`${formatTime(timeLeft)} remaining`}>
                                <circle className="prayer-timer-track" cx="100" cy="100" r="88" />
                                <circle
                                    className="prayer-timer-progress"
                                    cx="100"
                                    cy="100"
                                    r="88"
                                    strokeDasharray={TIMER_CIRCUMFERENCE}
                                    strokeDashoffset={progressOffset}
                                />
                            </svg>
                            <div className="prayer-timer-time">
                                <span>{timerStatus}</span>
                                <strong>{formatTime(timeLeft)}</strong>
                                <small>{Math.round(progressPercentage)}% complete</small>
                            </div>
                        </div>

                        <div className="prayer-timer-controls">
                            <button
                                type="button"
                                className="prayer-timer-control prayer-timer-control-secondary"
                                onClick={resetTimer}
                                aria-label="Reset prayer timer"
                            >
                                <RotateCcw size={19} />
                            </button>
                            {!isActive || isPaused ? (
                                <button
                                    type="button"
                                    className="prayer-timer-control prayer-timer-control-primary"
                                    onClick={() => {
                                        if (timeLeft === 0) setTimeLeft(duration);
                                        setIsActive(true);
                                        setIsPaused(false);
                                    }}
                                >
                                    <Play size={22} fill="currentColor" />
                                    <span>{isPaused ? 'Continue prayer' : 'Begin prayer'}</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="prayer-timer-control prayer-timer-control-primary"
                                    onClick={() => setIsPaused(true)}
                                >
                                    <Pause size={22} fill="currentColor" />
                                    <span>Pause</span>
                                </button>
                            )}
                            <button
                                type="button"
                                className={`prayer-timer-control prayer-timer-control-secondary ${showSettings ? 'is-selected' : ''}`}
                                onClick={() => setShowSettings(!showSettings)}
                                aria-label="Open timer settings"
                                aria-expanded={showSettings}
                            >
                                <Settings size={19} />
                            </button>
                        </div>
                    </div>

                    <div className="prayer-timer-focus">
                        <div className="prayer-timer-focus-icon"><Wind size={22} /></div>
                        <div>
                            <small>Praying for</small>
                            <h2>{prayerTopics[currentTopicIndex]}</h2>
                            <p>Inhale peace. Exhale what you cannot carry.</p>
                        </div>
                        <span className="prayer-timer-focus-count">{currentTopicIndex + 1} / {prayerTopics.length}</span>
                    </div>

                    <span className="prayer-timer-orbit prayer-timer-orbit-one" aria-hidden="true"></span>
                    <span className="prayer-timer-orbit prayer-timer-orbit-two" aria-hidden="true"></span>
                </section>

                {showSettings && (
                    <section className="prayer-timer-settings" aria-labelledby="timer-settings-heading">
                        <div className="prayer-timer-settings-heading">
                            <div>
                                <p className="prayer-timer-kicker">Make it yours</p>
                                <h2 id="timer-settings-heading">Timer settings</h2>
                            </div>
                            <button type="button" onClick={saveDuration}><Check size={18} /> Save settings</button>
                        </div>

                        <div className="prayer-timer-settings-grid">
                            <div className="prayer-timer-duration-editor">
                                <p><Clock3 size={17} /> Custom duration</p>
                                <div>
                                    <label>
                                        <span>Minutes</span>
                                        <input
                                            type="number"
                                            min="0"
                                            max="60"
                                            value={customMinutes}
                                            onChange={(event) => setCustomMinutes(event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Seconds</span>
                                        <input
                                            type="number"
                                            min="0"
                                            max="59"
                                            value={customSeconds}
                                            onChange={(event) => setCustomSeconds(event.target.value)}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="prayer-timer-topic-editor">
                                <p><Bell size={17} /> Prayer focuses</p>
                                <div>
                                    {prayerTopics.map((topic, index) => (
                                        <div className={`prayer-timer-topic-row ${index === currentTopicIndex ? 'is-current' : ''}`} key={index}>
                                            <button type="button" onClick={() => selectTopic(index)} aria-label={`Select ${topic}`}>
                                                {index === currentTopicIndex ? <Check size={15} /> : index + 1}
                                            </button>
                                            <input
                                                type="text"
                                                value={topic}
                                                onChange={(event) => updatePrayerTopic(index, event.target.value)}
                                                aria-label={`Prayer focus ${index + 1}`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removePrayerTopic(index)}
                                                disabled={prayerTopics.length <= 1}
                                                aria-label={`Remove ${topic}`}
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <button type="button" className="prayer-timer-add-topic" onClick={addPrayerTopic}>
                                    <Plus size={16} /> Add prayer focus
                                </button>
                            </div>
                        </div>
                    </section>
                )}

                <section className="prayer-timer-topics" aria-labelledby="prayer-focus-heading">
                    <div className="prayer-timer-section-heading">
                        <div>
                            <p className="prayer-timer-kicker">Guide your time</p>
                            <h2 id="prayer-focus-heading">Choose a prayer focus</h2>
                        </div>
                        <span>The next focus is selected when the bell sounds.</span>
                    </div>

                    <div className="prayer-timer-topic-grid">
                        {prayerTopics.map((topic, index) => (
                            <button
                                type="button"
                                key={index}
                                className={index === currentTopicIndex ? 'is-current' : ''}
                                onClick={() => selectTopic(index)}
                            >
                                <span>{String(index + 1).padStart(2, '0')}</span>
                                <strong>{topic}</strong>
                                {index === currentTopicIndex ? <Bell size={18} /> : <ChevronRight size={18} />}
                            </button>
                        ))}
                    </div>
                </section>

                <section className="prayer-timer-closing">
                    <div className="prayer-timer-breath-mark" aria-hidden="true"><Wind size={25} /></div>
                    <p className="prayer-timer-kicker">Before you begin</p>
                    <h2>Be still. Breathe slowly. Speak honestly.</h2>
                    <p>The timer is only a gentle boundary. Stay with a thought or prayer as long as you need.</p>
                </section>
            </div>
        </ScreenLayout>
    );
};

export default PrayerTimer;
